import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import TopNav from "../components/TopNav.jsx";

export default function LibraryShelfPage() {
  const { slug } = useParams();
  const [shelf, setShelf] = useState(null);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState("");
  const [hits, setHits] = useState(null);
  const [searching, setSearching] = useState(false);
  const [kindFilter, setKindFilter] = useState("all");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await api.libraryShelf(slug);
        if (!cancelled) setShelf(d);
      } catch (e) {
        if (!cancelled) setErr(e.message);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const doSearch = async (e) => {
    e?.preventDefault?.();
    if (!q.trim()) { setHits(null); return; }
    setSearching(true);
    try {
      const r = await api.libraryRetrieve(slug, q, 5);
      setHits(r);
    } catch (e2) {
      setHits({ hits: [], _error: e2.message });
    } finally {
      setSearching(false);
    }
  };

  const kinds = Array.from(new Set((shelf?.sources || []).map((s) => s.kind))).sort();
  const sources = (shelf?.sources || []).filter((s) => kindFilter === "all" || s.kind === kindFilter);

  return (
    <div className="min-h-screen bg-sage-bg text-sage-text sage-grain">
      <TopNav />
      <div className="mx-auto max-w-7xl px-4 md:px-8 py-8">
        <div className="mb-4 text-xs uppercase tracking-[0.15em]">
          <Link to="/library" data-testid="shelf-back" className="text-sage-muted hover:text-sage-accent">← Library</Link>
        </div>

        {err && <div className="border border-sage-err text-sage-err p-4 text-xs" data-testid="shelf-error">Failed to load: {err}</div>}
        {!shelf && !err && <div className="text-sage-faint text-xs" data-testid="shelf-loading">Opening shelf…</div>}

        {shelf && (
          <>
            <div className="border-b border-sage-border pb-6 mb-6">
              <div className="text-xs uppercase tracking-[0.2em] text-sage-muted mb-2">
                Shelf · {shelf.slug}
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tighter uppercase text-sage-text" data-testid="shelf-name">
                {shelf.name}
              </h1>
              <div className="text-sm text-sage-muted mt-3 max-w-4xl leading-relaxed" data-testid="shelf-description">
                {shelf.description}
              </div>
              <div className="flex flex-wrap gap-1 mt-3">
                {(shelf.tags || []).map((t) => (
                  <span key={t} className="text-[10px] uppercase tracking-[0.1em] text-sage-faint border border-sage-border px-2 py-0.5">
                    {t}
                  </span>
                ))}
              </div>
            </div>

            {/* The Bias Card — loud, unavoidable */}
            {shelf.bias_card && (
              <div className="border-2 border-sage-accent p-5 mb-8 bg-sage-accent/5" data-testid="shelf-bias-card">
                <div className="text-[10px] uppercase tracking-[0.2em] text-sage-accent font-bold mb-2">
                  ⓘ The Bias Card · Coffin Nail #28
                </div>
                <div className="text-sm text-sage-text leading-relaxed">
                  {shelf.bias_card}
                </div>
                <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint mt-3">
                  Curated by: {(shelf.curators || []).join(" · ")}
                </div>
              </div>
            )}

            {/* Ask the shelf — RAG retrieval preview */}
            <div className="border border-sage-border p-5 mb-8">
              <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted mb-3">
                Ask this shelf · lexical retrieval preview
              </div>
              <form onSubmit={doSearch} className="flex gap-2 flex-wrap">
                <input
                  type="text"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="e.g. philanthropy overflow ceiling"
                  data-testid="shelf-search-input"
                  className="border border-sage-border bg-sage-bg text-sage-text px-3 py-2 text-sm flex-1 min-w-64"
                />
                <button
                  type="submit"
                  disabled={searching}
                  data-testid="shelf-search-submit"
                  className="border border-sage-accent bg-sage-accent text-sage-bg px-4 py-2 uppercase tracking-[0.1em] text-xs hover:opacity-90 disabled:opacity-40"
                >
                  {searching ? "Searching…" : "Retrieve"}
                </button>
              </form>
              {hits && (
                <div className="mt-4" data-testid="shelf-hits">
                  <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint mb-2">
                    algo: {hits.algo || "lexical-overlap-v1"} · {hits.hits?.length || 0} hit{hits.hits?.length === 1 ? "" : "s"}
                  </div>
                  {(hits.hits || []).length === 0 ? (
                    <div className="text-sage-faint text-xs">No sources matched. Try different terms.</div>
                  ) : (
                    <div className="space-y-3">
                      {hits.hits.map((h) => (
                        <div key={h.source_id} className="border border-sage-border p-3 text-xs" data-testid={`shelf-hit-${h.source_id}`}>
                          <div className="flex justify-between items-baseline mb-1">
                            <div className="font-bold text-sage-text uppercase tracking-tight">{h.title}</div>
                            <div className="text-[10px] tabular-nums text-sage-accent">score {h.score}</div>
                          </div>
                          <div className="text-[10px] uppercase tracking-[0.1em] text-sage-faint mb-2">
                            {h.kind} · {h.url}
                          </div>
                          <div className="text-sage-muted whitespace-pre-wrap sage-prose">{h.excerpt}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Source list */}
            <div className="border border-sage-border" data-testid="shelf-sources">
              <div className="p-4 border-b border-sage-border text-xs uppercase tracking-[0.15em] text-sage-muted flex justify-between items-baseline flex-wrap gap-2">
                <span>Sources on this shelf</span>
                <div className="flex gap-2 items-center">
                  <span className="text-sage-faint tabular-nums">{sources.length} / {(shelf.sources || []).length}</span>
                  <select
                    value={kindFilter}
                    onChange={(e) => setKindFilter(e.target.value)}
                    data-testid="shelf-kind-filter"
                    className="border border-sage-border bg-sage-bg text-sage-text px-2 py-1 text-xs"
                  >
                    <option value="all">all kinds</option>
                    {kinds.map((k) => <option key={k} value={k}>{k}</option>)}
                  </select>
                </div>
              </div>
              <div className="divide-y divide-sage-border">
                {sources.map((s) => (
                  <div key={s.source_id} className="p-4 text-xs" data-testid={`shelf-source-${s.source_id}`}>
                    <div className="flex justify-between items-baseline mb-1 gap-2">
                      <div className="text-sm font-bold uppercase tracking-tight text-sage-text">{s.title}</div>
                      <div className="text-[10px] uppercase tracking-[0.1em] text-sage-faint">{s.kind}</div>
                    </div>
                    <div className="text-sage-muted mb-2 leading-relaxed">{s.summary}</div>
                    {s.url && (
                      <a href={s.url} target="_blank" rel="noreferrer" className="text-[10px] font-mono text-sage-accent hover:underline">
                        {s.url} ↗
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
